import React, { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "../../../firebaseConfig";
import {
  allocationPmcName,
  allocationPmcUid,
  canonicalPacsUid,
  canonicalPmcUid,
  currentAllocationMap,
  dedupePacs,
  dedupePmcs,
  isActiveAllocation,
  isApprovedPmc,
  isRemovedRecord,
  normalizeText,
  pacsDisplayName,
  pacsIdentityKeys,
  pmcDisplayName,
  recordSupportsState,
  resolvePmcReference,
  upperText,
} from "./branchDataUtils";

const PACS_ROLES = ["PACS", "PACS_SOCIETY", "PACS / SOCIETY"];

const formatDate = (value) => {
  if (!value) return "-";
  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const isApprovedPacs = (record = {}) =>
  !isRemovedRecord(record) &&
  record.canonicalIsActive !== false &&
  (upperText(record.canonicalApprovalStatus) === "APPROVED" ||
    [
      record.status,
      record.registrationStatus,
      record.roleApprovalStatus,
      record.branchApprovalStatus,
      record.approvalStatus,
    ].some((value) => upperText(value) === "APPROVED"));

const isNafedProject = (project = {}) =>
  project.nafedProject === true ||
  normalizeText(project.projectSource) === "nafed" ||
  project.applicationPortal === "NAFED_PACS_APPLY" ||
  upperText(project.applicationId).startsWith("NAFED-");

const recordsMatch = (left = {}, right = {}) => {
  const leftKeys = new Set(pacsIdentityKeys(left));
  return pacsIdentityKeys(right).some((key) => leftKeys.has(key));
};

const rowIsAllocated = (row = {}) =>
  Boolean(row.allocatedPmcUid || row.allocatedPmcName);

const firstLocationValue = (...values) =>
  values.find((value) => normalizeText(value)) || "";

/*
 * PACS data was created by more than one portal version. Location fields can
 * therefore be present on the profile, allocation or linked project. These
 * helpers provide one canonical value without changing any stored record.
 */
const districtOf = (record = {}) => {
  const linkedProjects = Array.isArray(record.projects) ? record.projects : [];
  const allocation = record.currentAllocation || {};
  return firstLocationValue(
    record.canonicalDistrict,
    record.district,
    record.districtName,
    record.locationDistrict,
    record.projectDistrict,
    record.registeredDistrict,
    record.address?.district,
    record.location?.district,
    record.projectLocation?.district,
    allocation.district,
    allocation.districtName,
    ...linkedProjects.flatMap((project) => [
      project.district,
      project.districtName,
      project.locationDistrict,
      project.projectDistrict,
      project.address?.district,
      project.location?.district,
      project.projectLocation?.district,
    ]),
  );
};

const talukaOf = (record = {}) => {
  const linkedProjects = Array.isArray(record.projects) ? record.projects : [];
  const allocation = record.currentAllocation || {};
  return firstLocationValue(
    record.canonicalTaluka,
    record.taluka,
    record.talukaName,
    record.block,
    record.blockName,
    record.tehsil,
    record.locationTaluka,
    record.address?.taluka,
    record.location?.taluka,
    allocation.taluka,
    allocation.block,
    ...linkedProjects.flatMap((project) => [
      project.taluka,
      project.talukaName,
      project.block,
      project.blockName,
      project.tehsil,
      project.locationTaluka,
      project.address?.taluka,
      project.location?.taluka,
    ]),
  );
};

const villageOf = (record = {}) => {
  const linkedProjects = Array.isArray(record.projects) ? record.projects : [];
  const allocation = record.currentAllocation || {};
  return firstLocationValue(
    record.canonicalVillage,
    record.village,
    record.villageName,
    record.locationVillage,
    record.projectVillage,
    record.address?.village,
    record.location?.village,
    allocation.village,
    allocation.locationVillage,
    ...linkedProjects.flatMap((project) => [
      project.village,
      project.villageName,
      project.locationVillage,
      project.projectVillage,
      project.address?.village,
      project.location?.village,
    ]),
  );
};

const UiIcon = ({ name, size = 22 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };
  if (name === "users") {
    return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
  }
  if (name === "database") {
    return <svg {...common}><ellipse cx="12" cy="5" rx="8" ry="3"/><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5"/><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"/></svg>;
  }
  if (name === "clock") {
    return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>;
  }
  if (name === "building") {
    return <svg {...common}><path d="M3 21h18M5 21V9l7-4 7 4v12M8 12h2M14 12h2M8 16h2M14 16h2"/></svg>;
  }
  if (name === "merge") {
    return <svg {...common}><path d="M7 3v5a4 4 0 0 0 4 4h6M7 21v-5a4 4 0 0 1 4-4M15 8l4 4-4 4"/></svg>;
  }
  if (name === "download") {
    return <svg {...common}><path d="M12 3v12M7 10l5 5 5-5M4 21h16"/></svg>;
  }
  if (name === "refresh") {
    return <svg {...common}><path d="M20 6v5h-5M4 18v-5h5"/><path d="M6.1 9a7 7 0 0 1 11.6-2.6L20 11M4 13l2.3 4.6A7 7 0 0 0 17.9 15"/></svg>;
  }
  if (name === "search") {
    return <svg {...common}><circle cx="11" cy="11" r="7"/><path d="m20 20-4-4"/></svg>;
  }
  if (name === "pin") {
    return <svg {...common}><path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="2.5"/></svg>;
  }
  if (name === "phone") {
    return <svg {...common}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.5 2.1L8 10a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c1 .3 1.9.6 2.9.7a2 2 0 0 1 1.7 2Z"/></svg>;
  }
  if (name === "check") {
    return <svg {...common}><path d="m5 12 4 4L19 6"/></svg>;
  }
  return <svg {...common}><rect x="4" y="4" width="6" height="6" rx="1"/><rect x="14" y="4" width="6" height="6" rx="1"/><rect x="4" y="14" width="6" height="6" rx="1"/><rect x="14" y="14" width="6" height="6" rx="1"/></svg>;
};

const formatLiveSync = (value) =>
  value
    ? value.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Waiting for first sync";

export default function BranchDistrictAllocation() {
  const [branch, setBranch] = useState(null);
  const [pmcs, setPmcs] = useState([]);
  const [pacs, setPacs] = useState([]);
  const [rawAllocations, setRawAllocations] = useState([]);
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [pmcFilter, setPmcFilter] = useState("ALL");
  const [rowPmc, setRowPmc] = useState({});
  const [rowAction, setRowAction] = useState({});
  const [busyId, setBusyId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastSynced, setLastSynced] = useState(null);
  const state = branch?.state || "";

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const user = auth.currentUser;
      if (!user) throw new Error("Please login as NAFED State Branch.");
      const profileSnap = await getDoc(doc(db, "nafed_users", user.uid));
      if (!profileSnap.exists()) throw new Error("State Branch profile not found.");
      const profile = { id: profileSnap.id, uid: user.uid, ...profileSnap.data() };
      if (
        !["STATE_BRANCH", "NAFED_STATE_BRANCH", "BRANCH_OFFICE"].includes(
          upperText(profile.role),
        )
      ) {
        throw new Error("Only State Branch can use this allocation desk.");
      }
      if (!profile.state) throw new Error("State is missing in Branch profile.");
      setBranch(profile);

      const [userSnap, allocationSnap, projectSnap] = await Promise.all([
        getDocs(collection(db, "nafed_users")),
        getDocs(collection(db, "pacs_pmc_allocations")),
        getDocs(collection(db, "godown_projects")),
      ]);
      const users = userSnap.docs.map((item) => ({ ...item.data(), id: item.id }));
      const allocations = allocationSnap.docs.map((item) => ({
        ...item.data(),
        id: item.id,
      }));
      const projects = projectSnap.docs
        .map((item) => ({ ...item.data(), id: item.id }))
        .filter(
          (project) =>
            !isRemovedRecord(project) &&
            isNafedProject(project) &&
            recordSupportsState(project, profile.state),
        );

      const statePmcs = dedupePmcs(
        users.filter(
          (record) =>
            ["PMC", "PMC_AGENCY"].includes(upperText(record.role)) &&
            recordSupportsState(record, profile.state) &&
            isApprovedPmc(record),
        ),
      );
      const currentMap = currentAllocationMap(allocations);
      const statePacs = dedupePacs(
        users.filter(
          (record) =>
            PACS_ROLES.includes(upperText(record.role)) &&
            recordSupportsState(record, profile.state),
        ),
      )
        .filter(isApprovedPacs)
        .map((record) => {
          const current = pacsIdentityKeys(record)
            .map((key) => currentMap.get(key))
            .find(Boolean);
          const linkedProjects = projects.filter((project) =>
            recordsMatch(record, project),
          );
          const projectAllocation = linkedProjects.find(
            (project) => allocationPmcUid(project) || allocationPmcName(project),
          );
          const activeCurrent = isActiveAllocation(current) ? current : null;
          const currentPmc = resolvePmcReference(
            activeCurrent || record || projectAllocation || {},
            statePmcs,
          );
          const allocationExists = Boolean(current);
          return {
            ...record,
            projects: linkedProjects,
            currentAllocation: current || null,
            canonicalDistrict: districtOf({
              ...record,
              projects: linkedProjects,
              currentAllocation: current || null,
            }),
            canonicalTaluka: talukaOf({
              ...record,
              projects: linkedProjects,
              currentAllocation: current || null,
            }),
            canonicalVillage: villageOf({
              ...record,
              projects: linkedProjects,
              currentAllocation: current || null,
            }),
            allocatedPmcUid:
              (currentPmc && canonicalPmcUid(currentPmc)) ||
              allocationPmcUid(activeCurrent) ||
              (!allocationExists &&
                (allocationPmcUid(record) || allocationPmcUid(projectAllocation))) ||
              "",
            allocatedPmcName:
              (currentPmc && pmcDisplayName(currentPmc)) ||
              allocationPmcName(activeCurrent) ||
              (!allocationExists &&
                (allocationPmcName(record) || allocationPmcName(projectAllocation))) ||
              "",
            pmcAllocatedAt:
              activeCurrent?.allocatedAt ||
              activeCurrent?.updatedAt ||
              record.pmcAllocatedAt ||
              null,
          };
        })
        .sort(
          (a, b) =>
            normalizeText(districtOf(a)).localeCompare(
              normalizeText(districtOf(b)),
            ) ||
            pacsDisplayName(a).localeCompare(pacsDisplayName(b)),
        );

      setPmcs(statePmcs);
      setPacs(statePacs);
      setRawAllocations(allocations);
      const selections = {};
      const actions = {};
      statePacs.forEach((record) => {
        selections[record.id] = record.allocatedPmcUid || "";
        actions[record.id] = rowIsAllocated(record) ? "KEEP" : "ALLOCATE";
      });
      setRowPmc(selections);
      setRowAction(actions);
      setLastSynced(new Date());
    } catch (loadError) {
      console.error("Branch allocation load failed:", loadError);
      setError(loadError.message || "Allocation data loading failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const stop = onAuthStateChanged(auth, (user) => {
      if (user) load();
      else {
        setLoading(false);
        setError("Please login as NAFED State Branch.");
      }
    });
    return stop;
  }, []);

  const districts = useMemo(
    () =>
      [
        ...new Set(
          pacs
            .map(districtOf)
            .map((value) => String(value || "").trim())
            .filter(Boolean),
        ),
      ].sort((a, b) => a.localeCompare(b, "en", { sensitivity: "base" })),
    [pacs],
  );

  const districtSummary = useMemo(
    () =>
      districts.map((districtName) => {
        const records = pacs.filter(
          (record) =>
            normalizeText(districtOf(record)) === normalizeText(districtName),
        );
        return {
          name: districtName,
          total: records.length,
          allocated: records.filter(rowIsAllocated).length,
          pending: records.filter((record) => !rowIsAllocated(record)).length,
        };
      }),
    [districts, pacs],
  );

  const counts = useMemo(
    () => ({
      total: pacs.length,
      allocated: pacs.filter(rowIsAllocated).length,
      pending: pacs.filter((record) => !rowIsAllocated(record)).length,
      pmcs: pmcs.length,
      duplicates: pacs.reduce(
        (total, record) => total + Number(record.duplicateCount || 0),
        0,
      ),
    }),
    [pacs, pmcs],
  );

  const visible = useMemo(
    () =>
      pacs.filter((record) => {
        const text = normalizeText(
          `${pacsDisplayName(record)} ${record.registrationId || ""} ${record.registrationNumber || ""} ${record.email || ""} ${record.mobile || ""} ${districtOf(record)} ${talukaOf(record)} ${villageOf(record)} ${record.allocatedPmcName || ""}`,
        );
        return (
          (district === "ALL" ||
            normalizeText(districtOf(record)) === normalizeText(district)) &&
          (status === "ALL" ||
            (status === "UNALLOCATED" && !rowIsAllocated(record)) ||
            (status === "ALLOCATED" && rowIsAllocated(record))) &&
          (pmcFilter === "ALL" ||
            (pmcFilter === "NONE" && !rowIsAllocated(record)) ||
            record.allocatedPmcUid === pmcFilter) &&
          (!normalizeText(search) || text.includes(normalizeText(search)))
        );
      }),
    [pacs, search, district, status, pmcFilter],
  );

  const applyAction = async (record) => {
    const action =
      rowAction[record.id] || (rowIsAllocated(record) ? "KEEP" : "ALLOCATE");
    if (action === "KEEP") {
      setError("Please select Allocate / Change PMC or Unallocate action.");
      return;
    }
    const deleting = action === "ARCHIVE_PACS";
    const chosenPmc = pmcs.find(
      (item) => canonicalPmcUid(item) === rowPmc[record.id],
    );
    const chosenPmcUid = chosenPmc ? canonicalPmcUid(chosenPmc) : "";
    const oldPmcUid = record.allocatedPmcUid || "";
    const workStarted = (record.projects || []).some(
      (project) =>
        project.workStartApproved === true ||
        project.constructionStarted === true ||
        Number(project.progressPercent || project.constructionProgress || 0) > 0,
    );

    if (action === "UNALLOCATE" && !rowIsAllocated(record)) {
      return setError("This PACS is already unallocated.");
    }
    if (!deleting && action !== "UNALLOCATE") {
      if (!chosenPmc) return setError("Please select an approved PMC in this row.");
      if (!recordSupportsState(chosenPmc, state)) {
        return setError("Selected PMC is outside the Branch State.");
      }
      if (oldPmcUid === chosenPmcUid) {
        return setError("This PACS is already assigned to the selected PMC.");
      }
    }
    if (workStarted && ["UNALLOCATE", "ALLOCATE"].includes(action) && oldPmcUid) {
      return setError(
        "Construction/work has started. PMC change requires Head Office escalation.",
      );
    }

    const event = deleting
      ? "PACS_ARCHIVED"
      : action === "UNALLOCATE"
        ? "UNALLOCATED"
        : oldPmcUid
          ? "REALLOCATED"
          : "ALLOCATED";
    const question = deleting
      ? `Archive ${pacsDisplayName(record)} from the operational allocation register?`
      : action === "UNALLOCATE"
        ? `Remove current PMC allocation from ${pacsDisplayName(record)}?`
        : `${event === "REALLOCATED" ? "Change" : "Allocate"} ${pacsDisplayName(record)} to ${pmcDisplayName(chosenPmc)}?`;
    if (!window.confirm(question)) return;

    try {
      setBusyId(record.id);
      setError("");
      setSuccess("");
      const user = auth.currentUser;
      if (!user) throw new Error("Session expired. Login again.");
      const latestBranchSnapshot = await getDoc(
        doc(db, "nafed_users", user.uid),
      );
      if (!latestBranchSnapshot.exists()) {
        throw new Error("State Branch profile not found.");
      }
      const latestBranch = latestBranchSnapshot.data();
      if (
        !["STATE_BRANCH", "NAFED_STATE_BRANCH", "BRANCH_OFFICE"].includes(
          upperText(latestBranch.role),
        )
      ) {
        throw new Error("Only State Branch can update PACS–PMC allocation.");
      }
      if (
        normalizeText(latestBranch.state || latestBranch.stateName) !==
        normalizeText(state)
      ) {
        throw new Error("Branch State changed. Refresh the allocation desk.");
      }
      if (latestBranch.isActive === false) {
        throw new Error("State Branch profile is inactive.");
      }
      const pacsUid = canonicalPacsUid(record);
      if (!pacsUid) throw new Error("Canonical PACS identity is missing.");
      const allocationRef = doc(db, "pacs_pmc_allocations", pacsUid);
      const batch = writeBatch(db);
      const clearedPmc = {
        allocatedPmcId: "",
        allocatedPmcUid: "",
        allocatedPmcName: "",
        allocatedPmcEmail: "",
        allocatedPmcMobile: "",
        pmcId: "",
        pmcUid: "",
        pmcName: "",
        pmcEmail: "",
        pmcMobile: "",
      };
      const chosenFields = chosenPmc
        ? {
            allocatedPmcId: chosenPmcUid,
            allocatedPmcUid: chosenPmcUid,
            allocatedPmcName: pmcDisplayName(chosenPmc),
            allocatedPmcEmail: chosenPmc.email || chosenPmc.officialEmail || "",
            allocatedPmcMobile: chosenPmc.mobile || chosenPmc.phone || "",
            pmcId: chosenPmcUid,
            pmcUid: chosenPmcUid,
            pmcName: pmcDisplayName(chosenPmc),
            pmcEmail: chosenPmc.email || chosenPmc.officialEmail || "",
            pmcMobile: chosenPmc.mobile || chosenPmc.phone || "",
          }
        : clearedPmc;
      const baseIdentity = {
        pacsUid,
        pacsId: pacsUid,
        pacsUserUid: pacsUid,
        pacsUserId: pacsUid,
        allocatedPacsUid: pacsUid,
        allocatedPacsId: pacsUid,
        societyUid: record.societyUid || pacsUid,
        pacsName: pacsDisplayName(record),
        societyName: record.societyName || pacsDisplayName(record),
        organizationName: record.organizationName || pacsDisplayName(record),
        pacsRegistrationNo:
          record.pacsRegistrationNo || record.registrationNumber || "",
        registrationNumber:
          record.registrationNumber || record.pacsRegistrationNo || "",
        pacsEmail: record.email || "",
        pacsMobile: record.mobile || record.phone || "",
        state,
        district: districtOf(record),
        taluka: talukaOf(record),
        village: villageOf(record),
      };
      const operationalFields = deleting
        ? {
            ...clearedPmc,
            status: "ARCHIVED_FROM_DESK",
            allocationStatus: "ARCHIVED",
            isActive: false,
            isDeleted: true,
            archivedAt: serverTimestamp(),
          }
        : action === "UNALLOCATE"
          ? {
              ...clearedPmc,
              status: "UNALLOCATED",
              allocationStatus: "UNALLOCATED",
              pmcAllocationStatus: "UNALLOCATED",
              isActive: false,
              isDeleted: false,
              unallocatedAt: serverTimestamp(),
            }
          : {
              ...chosenFields,
              status: "PENDING_PMC_APPROVAL",
              allocationStatus: "ACTIVE",
              pmcAllocationStatus: "ALLOCATED",
              branchApprovalStatus: "APPROVED",
              roleApprovalStatus: "APPROVED",
              registrationStatus: "PENDING_PMC_APPROVAL",
              approvalStage: "PMC_VERIFICATION",
              isActive: true,
              isDeleted: false,
              allocatedAt: serverTimestamp(),
              pmcAllocatedAt: serverTimestamp(),
            };
      const updateFields = {
        ...operationalFields,
        allocationMode: "PACS_WISE",
        previousPmcUid: oldPmcUid,
        previousPmcName: record.allocatedPmcName || "",
        allocatedByUid: user.uid,
        allocatedByEmail: user.email || "",
        allocatedByRole: "STATE_BRANCH",
        allocationVersion:
          Number(record.currentAllocation?.allocationVersion || 0) + 1,
        updatedAt: serverTimestamp(),
      };
      const allocationDocumentFields =
        action === "ALLOCATE" && !deleting
          ? { ...updateFields, status: "ACTIVE" }
          : updateFields;
      const projectUpdateFields = Object.fromEntries(
        Object.entries(updateFields).filter(
          ([field]) =>
            ![
              "status",
              "registrationStatus",
              "branchApprovalStatus",
              "roleApprovalStatus",
              "approvalStage",
              "isDeleted",
            ].includes(field),
        ),
      );

      batch.set(
        allocationRef,
        { ...baseIdentity, ...allocationDocumentFields },
        { merge: true },
      );
      (record.sourceIds?.length ? record.sourceIds : [record.id]).forEach(
        (profileId) =>
          batch.set(doc(db, "nafed_users", profileId), updateFields, {
            merge: true,
          }),
      );
      (record.projects || []).forEach((project) =>
        batch.set(doc(db, "godown_projects", project.id), projectUpdateFields, {
          merge: true,
        }),
      );

      rawAllocations
        .filter(
          (allocation) =>
            allocation.id !== pacsUid &&
            isActiveAllocation(allocation) &&
            recordSupportsState(allocation, state) &&
            recordsMatch(record, allocation),
        )
        .slice(0, 250)
        .forEach((allocation) =>
          batch.set(
            doc(db, "pacs_pmc_allocations", allocation.id),
            {
              status: "SUPERSEDED",
              allocationStatus: "SUPERSEDED",
              isActive: false,
              state,
              allocatedByUid: user.uid,
              allocatedByRole: "STATE_BRANCH",
              supersededBy: pacsUid,
              supersededAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true },
          ),
        );

      const auditRef = doc(collection(db, "nafed_activity"));
      const auditAction = {
        ALLOCATED: "STATE_BRANCH_PACS_ALLOCATED_TO_PMC",
        REALLOCATED: "STATE_BRANCH_PACS_REALLOCATED_TO_PMC",
        UNALLOCATED: "STATE_BRANCH_PACS_UNALLOCATED",
        PACS_ARCHIVED: "STATE_BRANCH_PACS_ARCHIVED",
      }[event];
      batch.set(auditRef, {
        action: auditAction,
        targetUid: pacsUid,
        targetRole: "PACS",
        targetName: pacsDisplayName(record),
        state,
        district: districtOf(record),
        previousPmcUid: oldPmcUid,
        previousPmcName: record.allocatedPmcName || "",
        allocatedPmcUid: chosenPmcUid,
        allocatedPmcName: chosenPmc ? pmcDisplayName(chosenPmc) : "",
        performedByUid: user.uid,
        performedByEmail: user.email || "",
        performedByRole: "STATE_BRANCH",
        createdAt: serverTimestamp(),
      });
      if (chosenPmc && !deleting && action !== "UNALLOCATE") {
        const mailRef = doc(collection(db, "nafed_mail_notifications"));
        batch.set(mailRef, {
          type:
            event === "REALLOCATED"
              ? "PACS_REALLOCATED_TO_PMC"
              : "PACS_ALLOCATED_TO_PMC",
          to: chosenPmc.email || "",
          recipientUid: chosenPmcUid,
          subject: `PACS allocated for review - ${pacsDisplayName(record)}`,
          message: `${pacsDisplayName(record)}, ${districtOf(record) || state}, has been allocated to your PMC.`,
          status: "PENDING",
          relatedCollection: "nafed_users",
          relatedId: pacsUid,
          state,
          district: districtOf(record),
          createdAt: serverTimestamp(),
        });
      }
      await batch.commit();

      setSuccess(
        deleting
          ? `${pacsDisplayName(record)} archived from the operational register.`
          : action === "UNALLOCATE"
            ? `${pacsDisplayName(record)} is now unallocated.`
            : `${pacsDisplayName(record)} successfully ${event.toLowerCase()} to ${pmcDisplayName(chosenPmc)}.`,
      );
      await load();
    } catch (actionError) {
      console.error("Branch allocation update failed:", actionError);
      setError(
        actionError?.message ||
          "Allocation update failed. Check State Branch Firestore permissions.",
      );
    } finally {
      setBusyId("");
    }
  };

  const resetFilters = () => {
    setSearch("");
    setDistrict("ALL");
    setStatus("ALL");
    setPmcFilter("ALL");
  };

  const downloadCsv = () => {
    const cell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const rows = [
      [
        "Sr",
        "PACS / Society",
        "Registration",
        "District",
        "Taluka",
        "Village",
        "Current PMC",
        "Status",
      ],
      ...visible.map((record, index) => [
        index + 1,
        pacsDisplayName(record),
        record.registrationNumber || record.registrationId || record.id,
        districtOf(record),
        talukaOf(record),
        villageOf(record),
        record.allocatedPmcName || "",
        rowIsAllocated(record) ? "ALLOCATED" : "UNALLOCATED",
      ]),
    ];
    const blob = new Blob(
      [rows.map((row) => row.map(cell).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `NAFED_${state || "State"}_PACS_PMC_Allocation.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="allocation-register">
      <style>{`${ALLOCATION_STYLE}\n${PREMIUM_ALLOCATION_STYLE}`}</style>
      <section className="allocation-hero">
        <div className="hero-copy">
          <small>NAFED STATE BRANCH · CANONICAL ALLOCATION CONTROL</small>
          <h1>PACS–PMC Allocation Register</h1>
          <p>
            View and manage PACS allocations to approved Project Management
            Consultancies across districts. Every update maintains one canonical,
            audit-ready allocation record.
          </p>
        </div>
        <div className="hero-actions">
          <div className="live-data-card">
            <strong><i /> Live Data</strong>
            <small>Last synced: {formatLiveSync(lastSynced)}</small>
          </div>
          <button onClick={downloadCsv}>
            <UiIcon name="download" size={17} /> Export CSV
          </button>
          <button className="primary" onClick={load} disabled={loading}>
            <UiIcon name="refresh" size={17} />
            {loading ? "Refreshing..." : "Refresh Live Data"}
          </button>
        </div>
      </section>

      {error && <div className="allocation-message error">{error}</div>}
      {success && <div className="allocation-message success">{success}</div>}

      <section className="allocation-kpis">
        <article className="kpi-green">
          <div className="kpi-icon"><UiIcon name="users" /></div>
          <div><span>Approved State PACS</span><b>{counts.total}</b>
          <small>Total PACS approved for allocation</small></div>
        </article>
        <article className="kpi-blue">
          <div className="kpi-icon"><UiIcon name="database" /></div>
          <div><span>Allocated to PMC</span><b>{counts.allocated}</b>
          <small>PACS successfully allocated</small></div>
        </article>
        <article className="kpi-orange">
          <div className="kpi-icon"><UiIcon name="clock" /></div>
          <div><span>Awaiting Allocation</span><b>{counts.pending}</b>
          <small>PACS pending allocation</small></div>
        </article>
        <article className="kpi-emerald">
          <div className="kpi-icon"><UiIcon name="building" /></div>
          <div><span>Approved Active PMCs</span><b>{counts.pmcs}</b>
          <small>PMCs available for allocation</small></div>
        </article>
        <article className="kpi-amber">
          <div className="kpi-icon"><UiIcon name="merge" /></div>
          <div><span>Duplicates Merged</span><b>{counts.duplicates}</b>
          <small>Duplicate records consolidated</small></div>
        </article>
      </section>

      <section className="allocation-panel">
        <div className="allocation-management-bar">
          <div className="allocation-panel-head">
            <h2>Live Allocation Management</h2>
            <p>Search, filter and manage PACS allocations in real time.</p>
          </div>
          <div className="allocation-filters">
          <label>
            <span>Search PACS / location / PMC</span>
            <div className="search-control">
              <UiIcon name="search" size={17} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by PACS name, village or code..." />
            </div>
          </label>
          <label>
            <span>District</span>
            <select value={district} onChange={(event) => setDistrict(event.target.value)}>
              <option value="ALL">All Districts</option>
              {districts.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label>
            <span>Allocation Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="ALL">All Status</option>
              <option value="UNALLOCATED">Unallocated</option>
              <option value="ALLOCATED">Allocated</option>
            </select>
          </label>
          <label>
            <span>Allocated PMC</span>
            <select value={pmcFilter} onChange={(event) => setPmcFilter(event.target.value)}>
              <option value="ALL">All PMCs</option>
              <option value="NONE">No PMC</option>
              {pmcs.map((item) => <option key={canonicalPmcUid(item)} value={canonicalPmcUid(item)}>{pmcDisplayName(item)}</option>)}
            </select>
          </label>
          <button onClick={resetFilters}>Reset</button>
          </div>
        </div>

        <section className="district-filter-section">
          <div className="district-filter-heading">
            <div>
              <span className="district-title-icon"><UiIcon name="pin" size={23} /></span>
              <p><strong>District-wise PACS Register</strong>
              <small>Select a district to view and manage only its PACS allocations.</small></p>
            </div>
            <em>{district === "ALL" ? "ALL DISTRICTS" : district}</em>
          </div>
          <div className="district-filter-strip" aria-label="District-wise PACS filter">
            <button
              type="button"
              className={district === "ALL" ? "active" : ""}
              onClick={() => setDistrict("ALL")}
            >
              <span className="district-card-title"><i><UiIcon name="grid" size={19} /></i><b>All Districts</b>{district === "ALL" && <em><UiIcon name="check" size={14} /></em>}</span>
              <span className="district-stat"><small>PACS Total</small><strong>{pacs.length}</strong></span>
              <span className="district-stat"><small>Allocated</small><strong>{counts.allocated}</strong></span>
              <span className="district-stat"><small>Pending</small><strong>{counts.pending}</strong></span>
            </button>
            {districtSummary.map((item) => (
              <button
                type="button"
                key={item.name}
                className={
                  normalizeText(district) === normalizeText(item.name)
                    ? "active"
                    : ""
                }
                onClick={() => setDistrict(item.name)}
              >
                <span className="district-card-title"><i><UiIcon name="grid" size={19} /></i><b>{item.name}</b>{normalizeText(district) === normalizeText(item.name) && <em><UiIcon name="check" size={14} /></em>}</span>
                <span className="district-stat"><small>PACS Total</small><strong>{item.total}</strong></span>
                <span className="district-stat"><small>Allocated</small><strong>{item.allocated}</strong></span>
                <span className="district-stat"><small>Pending</small><strong>{item.pending}</strong></span>
              </button>
            ))}
          </div>
        </section>

        <div className="allocation-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Sr.</th><th>PACS / Society</th><th>Location & Contact</th>
                <th>Current Allocation</th><th>Select Approved PMC</th>
                <th>Action</th><th>Update</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="empty">Loading canonical allocation register...</td></tr>
              ) : visible.length ? (
                visible.map((record, index) => (
                  <tr key={record.id}>
                    <td><span className="serial-number">{index + 1}</span></td>
                    <td>
                      <b>{pacsDisplayName(record)}</b>
                      <small>PACS Code: {record.registrationNumber || record.registrationId || record.id}</small>
                      {record.duplicateCount > 0 && <em>{record.duplicateCount + 1} profiles merged</em>}
                    </td>
                    <td>
                      <span className="location-line"><UiIcon name="pin" size={14} /><b>{[villageOf(record), talukaOf(record), districtOf(record), state].filter(Boolean).join(", ") || "Location pending"}</b></span>
                      <span className="location-line muted"><UiIcon name="phone" size={13} /><small>{record.mobile || record.phone || "Contact pending"}{record.email ? ` · ${record.email}` : ""}</small></span>
                    </td>
                    <td>
                      <span className={`allocation-badge ${rowIsAllocated(record) ? "allocated" : ""}`}>{record.allocatedPmcName || "UNALLOCATED"}</span>
                      <small>{rowIsAllocated(record) ? `Allocated ${formatDate(record.pmcAllocatedAt)}` : "Branch action pending"}</small>
                    </td>
                    <td>
                      <select value={rowPmc[record.id] || ""} disabled={["UNALLOCATE", "ARCHIVE_PACS"].includes(rowAction[record.id])} onChange={(event) => setRowPmc((current) => ({ ...current, [record.id]: event.target.value }))}>
                        <option value="">Select approved PMC</option>
                        {pmcs.map((item) => <option key={canonicalPmcUid(item)} value={canonicalPmcUid(item)}>{pmcDisplayName(item)}{item.email ? ` — ${item.email}` : ""}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={rowAction[record.id] || (rowIsAllocated(record) ? "KEEP" : "ALLOCATE")} onChange={(event) => setRowAction((current) => ({ ...current, [record.id]: event.target.value }))}>
                        <option value="KEEP">Select action</option>
                        <option value="ALLOCATE">{rowIsAllocated(record) ? "Change PMC" : "Allocate PMC"}</option>
                        {rowIsAllocated(record) && <option value="UNALLOCATE">Unallocate</option>}
                        <option value="ARCHIVE_PACS">Archive from desk</option>
                      </select>
                    </td>
                    <td>
                      <button className="update-button" disabled={busyId === record.id} onClick={() => applyAction(record)}>
                        {busyId === record.id ? "Updating..." : <><UiIcon name="check" size={15} />Apply Update</>}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="empty">No approved PACS matches the selected filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <footer>
          <span>Allocated: {counts.allocated} / {counts.total}</span>
          <span>Showing {visible.length} canonical PACS records</span>
        </footer>
      </section>
    </div>
  );
}

const ALLOCATION_STYLE = `
.allocation-register{--navy:#082d52;--green:#168447;--orange:#ea7d13;--text:#102a43;--muted:#61778c;--line:#dbe4ec;display:grid;gap:13px;color:var(--text);font-family:Inter,"Segoe UI",Arial,sans-serif}.allocation-register *{box-sizing:border-box}.allocation-hero{min-height:108px;padding:18px 20px;display:flex;align-items:center;justify-content:space-between;gap:20px;border:1px solid var(--line);border-left:4px solid var(--green);border-radius:10px;background:#fff;box-shadow:0 6px 22px rgba(8,45,82,.055)}.allocation-hero small{color:var(--green);font-size:8px;font-weight:900;letter-spacing:.08em}.allocation-hero h1{margin:6px 0 4px;font-size:21px}.allocation-hero p{max-width:760px;margin:0;color:var(--muted);font-size:9px;line-height:1.55}.hero-actions{display:flex;gap:8px}.hero-actions button,.allocation-filters>button{min-height:38px;padding:0 12px;border:1px solid #9fb3c5;border-radius:6px;background:#fff;color:var(--navy);font-size:8px;font-weight:850;cursor:pointer}.hero-actions button.primary{border-color:var(--navy);background:var(--navy);color:#fff}.allocation-message{padding:11px 13px;border-radius:7px;font-size:9px;font-weight:800}.allocation-message.error{border:1px solid #efaaaa;background:#fff1f1;color:#a12626}.allocation-message.success{border:1px solid #93d0aa;background:#edf9f2;color:#146239}.allocation-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:9px}.allocation-kpis article{min-height:90px;padding:13px 14px;border:1px solid var(--line);border-top:3px solid var(--green);border-radius:9px;background:#fff;box-shadow:0 4px 14px rgba(8,45,82,.04)}.allocation-kpis article:nth-child(2){border-top-color:#2563d8}.allocation-kpis article:nth-child(3){border-top-color:var(--orange)}.allocation-kpis article:nth-child(4){border-top-color:#7c3fda}.allocation-kpis article:nth-child(5){border-top-color:#60758a}.allocation-kpis span,.allocation-kpis b,.allocation-kpis small{display:block}.allocation-kpis span{color:#60758a;font-size:7.5px;font-weight:900;text-transform:uppercase}.allocation-kpis b{margin:6px 0 4px;font-size:21px}.allocation-kpis small{color:#8493a3;font-size:7px}.allocation-panel{overflow:hidden;border:1px solid var(--line);border-radius:10px;background:#fff;box-shadow:0 7px 24px rgba(8,45,82,.055)}.allocation-panel-head{padding:13px 15px;border-bottom:1px solid var(--line)}.allocation-panel-head h2{margin:0;font-size:15px}.allocation-panel-head p{margin:4px 0 0;color:var(--muted);font-size:8px}.allocation-filters{padding:10px;display:grid;grid-template-columns:minmax(240px,1.35fr) repeat(3,minmax(150px,.7fr)) auto;gap:8px;border-bottom:1px solid var(--line);background:#f6f9fb}.allocation-filters label{display:grid;gap:5px}.allocation-filters label>span{color:#50677d;font-size:7px;font-weight:900;text-transform:uppercase}.allocation-filters input,.allocation-filters select,.allocation-table-wrap select{width:100%;height:37px;padding:0 9px;border:1px solid #bdcad5;border-radius:6px;background:#fff;color:var(--text);font-size:8px}.allocation-filters>button{align-self:end}.district-filter-strip{display:flex;gap:8px;padding:10px;overflow-x:auto;border-bottom:1px solid var(--line);background:#fff}.district-filter-strip button{min-width:150px;padding:9px 11px;border:1px solid #cbd7e1;border-radius:8px;background:#f8fafc;color:var(--navy);text-align:left;cursor:pointer;transition:.16s ease}.district-filter-strip button:hover{border-color:#78a88e;background:#f2faf5}.district-filter-strip button.active{border-color:var(--green);background:#eaf7ef;box-shadow:inset 0 0 0 1px var(--green)}.district-filter-strip b,.district-filter-strip span{display:block}.district-filter-strip b{font-size:11px;white-space:nowrap}.district-filter-strip span{margin-top:4px;color:#677d90;font-size:9px;white-space:nowrap}.allocation-table-wrap{overflow:auto}.allocation-table-wrap table{width:100%;min-width:1320px;border-collapse:separate;border-spacing:0}.allocation-table-wrap th{padding:9px 10px;border-right:1px solid var(--line);border-bottom:1px solid #c7d4df;background:#eef3f7;color:var(--navy);font-size:7px;text-align:left;text-transform:uppercase;letter-spacing:.035em}.allocation-table-wrap td{padding:9px 10px;border-right:1px solid #edf1f4;border-bottom:1px solid #e5ebf0;color:#334e68;font-size:8.5px;vertical-align:middle}.allocation-table-wrap tr:last-child td{border-bottom:0}.allocation-table-wrap tbody tr:hover td{background:#f8fbfd}.allocation-table-wrap td b,.allocation-table-wrap td small{display:block}.allocation-table-wrap td small{margin-top:4px;color:#718397;font-size:7px}.allocation-table-wrap td em{display:inline-flex;margin-top:5px;padding:3px 6px;border-radius:99px;background:#edf4ff;color:#245a9a;font-size:6.5px;font-style:normal;font-weight:900}.allocation-badge{display:inline-flex;padding:5px 7px;border-radius:99px;background:#fff4df;color:#91590b;font-size:7px;font-weight:900}.allocation-badge.allocated{background:#e8f7ed;color:#12683b}.update-button{min-height:34px;padding:0 10px;border:1px solid var(--green);border-radius:6px;background:var(--green);color:#fff;font-size:7.5px;font-weight:850;cursor:pointer}.update-button:disabled{opacity:.5;cursor:not-allowed}.empty{padding:30px!important;text-align:center;color:var(--muted)}.allocation-panel footer{padding:9px 12px;display:flex;justify-content:space-between;gap:12px;border-top:1px solid var(--line);background:#f8fafc;color:var(--muted);font-size:7.5px}@media(max-width:1200px){.allocation-kpis{grid-template-columns:repeat(3,1fr)}.allocation-filters{grid-template-columns:repeat(2,1fr)}}@media(max-width:720px){.allocation-hero{align-items:flex-start;flex-direction:column}.hero-actions{width:100%}.hero-actions button{flex:1}.allocation-kpis,.allocation-filters{grid-template-columns:1fr}.district-filter-strip button{min-width:165px}.allocation-panel footer{flex-direction:column}}
.allocation-hero small,.allocation-kpis span,.allocation-filters label>span{font-size:10px}.allocation-hero p,.allocation-message,.allocation-panel-head p{font-size:12px}.hero-actions button,.allocation-filters>button,.allocation-filters input,.allocation-filters select,.allocation-table-wrap select{font-size:11px}.allocation-kpis small,.allocation-table-wrap td small{font-size:10px}.allocation-table-wrap th{font-size:10px}.allocation-table-wrap td{font-size:11px}.allocation-table-wrap td em,.allocation-badge,.update-button,.allocation-panel footer{font-size:10px}
.district-filter-section{border-bottom:1px solid var(--line);background:#fff}.district-filter-heading{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:12px 14px 3px}.district-filter-heading strong,.district-filter-heading small{display:block}.district-filter-heading strong{color:var(--navy);font-size:13px}.district-filter-heading small{margin-top:4px;color:var(--muted);font-size:10px}.district-filter-heading em{padding:6px 9px;border-radius:99px;background:#eaf7ef;color:#12683b;font-size:9px;font-style:normal;font-weight:900}.district-filter-section .district-filter-strip{border-bottom:0}.district-filter-section+.allocation-table-wrap{border-top:1px solid #edf1f4}@media(max-width:720px){.district-filter-heading{align-items:flex-start;flex-direction:column}.district-filter-heading em{align-self:flex-start}}
`;

const PREMIUM_ALLOCATION_STYLE = `
.allocation-register{
  --navy:#082d52;--navy-2:#123f71;--green:#159447;--green-dark:#0d7136;
  --orange:#f08a16;--blue:#0d6fc4;--text:#102a43;--muted:#667b91;
  --line:#d9e3ec;--soft:#f5f8fb;display:grid;gap:16px;width:100%;
  color:var(--text);font-family:Inter,"Segoe UI",Arial,sans-serif;
}
.allocation-register *{box-sizing:border-box}
.allocation-hero{
  min-height:138px;padding:24px 28px;display:flex;align-items:center;
  justify-content:space-between;gap:28px;border:1px solid var(--line);
  border-radius:12px;background:linear-gradient(135deg,#fff 0%,#fbfdff 100%);
  box-shadow:0 7px 24px rgba(8,45,82,.06);
}
.hero-copy{min-width:0;flex:1}.allocation-hero .hero-copy>small{
  color:#536b83;font-size:11px;font-weight:900;letter-spacing:.08em;
}
.allocation-hero h1{margin:7px 0 7px;color:#0b2e5d;font-size:31px;line-height:1.1;letter-spacing:-.025em}
.allocation-hero p{max-width:850px;margin:0;color:#61758a;font-size:13px;line-height:1.55}
.hero-actions{display:flex;align-items:center;justify-content:flex-end;gap:12px;flex-wrap:wrap}
.hero-actions button{
  min-height:46px;padding:0 18px;display:inline-flex;align-items:center;justify-content:center;
  gap:9px;border:1px solid #9fb4c6;border-radius:8px;background:#fff;color:var(--navy);
  font-size:12px;font-weight:850;white-space:nowrap;cursor:pointer;box-shadow:0 2px 5px rgba(8,45,82,.035)
}
.hero-actions button:hover{border-color:#65839c;background:#f8fbfd}.hero-actions button.primary{
  border-color:var(--navy);background:var(--navy);color:#fff;box-shadow:0 6px 14px rgba(8,45,82,.16)
}
.hero-actions button.primary:hover{background:#0d3a67}.hero-actions button:disabled{opacity:.58;cursor:not-allowed}
.live-data-card{
  min-height:46px;padding:8px 13px;border:1px solid #d2eadb;border-radius:9px;
  background:#effaf3;color:var(--green-dark);white-space:nowrap
}
.live-data-card strong,.live-data-card small{display:block}.live-data-card strong{font-size:11px;text-transform:none}
.live-data-card strong i{display:inline-block;width:8px;height:8px;margin-right:6px;border-radius:50%;background:#31bc64;box-shadow:0 0 0 4px rgba(49,188,100,.11)}
.live-data-card small{margin-top:4px;color:#648171;font-size:8px!important;font-weight:600}
.allocation-message{padding:13px 16px;border-radius:9px;font-size:12px;font-weight:800}
.allocation-kpis{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:14px}
.allocation-kpis article{
  min-height:112px;padding:16px;display:flex;align-items:center;gap:14px;border:1px solid var(--line)!important;
  border-top:1px solid var(--line)!important;border-radius:10px;background:#fff;
  box-shadow:0 4px 15px rgba(8,45,82,.045)
}
.allocation-kpis .kpi-icon{
  width:52px;height:52px;display:flex;align-items:center;justify-content:center;flex:0 0 52px;
  border-radius:13px;background:#e9f7ee;color:#128546
}
.allocation-kpis .kpi-blue .kpi-icon{background:#eaf4ff;color:#0c70c5}
.allocation-kpis .kpi-orange .kpi-icon{background:#fff2df;color:#ea7b09}
.allocation-kpis .kpi-emerald .kpi-icon{background:#e9f8ef;color:#097c3c}
.allocation-kpis .kpi-amber .kpi-icon{background:#fff2dc;color:#e8830a}
.allocation-kpis article>div:last-child{min-width:0}.allocation-kpis span{
  color:#455d75;font-size:11px!important;font-weight:750;text-transform:none
}
.allocation-kpis b{margin:4px 0 3px;color:#092c58;font-size:28px;line-height:1}
.allocation-kpis small{color:#7b8d9f;font-size:9px!important;line-height:1.35}
.allocation-panel{overflow:hidden;border:1px solid var(--line);border-radius:12px;background:#fff;box-shadow:0 8px 25px rgba(8,45,82,.055)}
.allocation-management-bar{
  padding:16px 18px;display:grid;grid-template-columns:minmax(250px,.8fr) minmax(680px,2.2fr);
  align-items:end;gap:18px;border-bottom:1px solid var(--line);background:#fff
}
.allocation-panel-head{padding:0;border:0}.allocation-panel-head h2{margin:0;color:#0b2e5d;font-size:20px;letter-spacing:-.015em}
.allocation-panel-head p{margin:5px 0 0;color:#71859a;font-size:11px!important}
.allocation-filters{
  padding:0;display:grid;grid-template-columns:minmax(240px,1.3fr) repeat(3,minmax(145px,.7fr)) auto;
  align-items:end;gap:10px;border:0;background:transparent
}
.allocation-filters label{display:grid;gap:6px}.allocation-filters label>span{
  color:#4f657a;font-size:9px!important;font-weight:850;text-transform:none
}
.allocation-filters input,.allocation-filters select,.allocation-table-wrap select{
  width:100%;height:42px;padding:0 11px;border:1px solid #bdcad5;border-radius:7px;
  outline:0;background:#fff;color:var(--text);font-size:11px!important;transition:.15s ease
}
.allocation-filters input:focus,.allocation-filters select:focus,.allocation-table-wrap select:focus{
  border-color:#2b73b9;box-shadow:0 0 0 3px rgba(43,115,185,.1)
}
.search-control{position:relative}.search-control>svg{position:absolute;top:50%;left:12px;z-index:1;transform:translateY(-50%);color:#365f87}
.search-control input{padding-left:38px}.allocation-filters>button{
  min-height:42px;padding:0 13px;border:1px solid #9fb3c5;border-radius:7px;background:#fff;
  color:var(--navy);font-size:10px!important;font-weight:850;cursor:pointer
}
.district-filter-section{margin:14px;border:1px solid #42c875;border-radius:11px;background:linear-gradient(90deg,#f4fff8 0%,#effbf4 100%);overflow:hidden}
.district-filter-heading{padding:14px 16px 7px;display:flex;align-items:center;justify-content:space-between;gap:18px}
.district-filter-heading>div{display:flex;align-items:center;gap:11px}.district-filter-heading p{margin:0}.district-filter-heading strong,.district-filter-heading small{display:block}
.district-filter-heading strong{color:#116839;font-size:18px}.district-filter-heading small{margin-top:4px;color:#496f5a;font-size:10px}
.district-title-icon{width:34px;height:34px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#dff6e8;color:#13934a}
.district-filter-heading>em{padding:7px 13px;border-radius:99px;background:#178f48;color:#fff;font-size:9px;font-style:normal;font-weight:900;letter-spacing:.035em}
.district-filter-strip{display:flex;gap:12px;padding:10px 14px 14px;overflow-x:auto;border:0;background:transparent;scrollbar-color:#a9cdb8 transparent;scrollbar-width:thin}
.district-filter-strip button{
  min-width:218px;padding:13px 14px;border:1px solid #cddce5;border-radius:8px;background:#fff;
  color:var(--navy);text-align:left;cursor:pointer;transition:.16s ease;box-shadow:0 2px 7px rgba(8,45,82,.025)
}
.district-filter-strip button:hover{transform:translateY(-1px);border-color:#72bd91;background:#fff}
.district-filter-strip button.active{border-color:#159447;background:linear-gradient(135deg,#f5fff8,#dff7e8);box-shadow:inset 0 0 0 1px #159447,0 4px 11px rgba(21,148,71,.09)}
.district-card-title{display:flex!important;align-items:center;gap:8px;margin-bottom:10px}.district-card-title i{
  width:29px;height:29px;display:flex;align-items:center;justify-content:center;border-radius:7px;background:#e7f2ff;color:#0b70c7;font-style:normal
}
.district-filter-strip button.active .district-card-title i{background:#dbf4e5;color:#138447}.district-card-title b{flex:1;font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.district-card-title>em{width:24px;height:24px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:#169248;color:#fff;font-style:normal}
.district-stat{display:flex!important;align-items:center;justify-content:space-between;margin-top:4px}.district-stat small{color:#62798d;font-size:10px}.district-stat strong{color:#0b2e5d;font-size:11px}.district-filter-strip button.active .district-stat strong{color:#116839}
.allocation-table-wrap{overflow:auto;border-top:1px solid #e1e9ef}.allocation-table-wrap table{width:100%;min-width:1420px;border-collapse:separate;border-spacing:0}
.allocation-table-wrap th{padding:12px 13px;border-right:1px solid #d9e3ec;border-bottom:1px solid #c5d3df;background:#eaf1f7;color:#183f69;font-size:9px!important;font-weight:900;text-align:left;text-transform:none;letter-spacing:.015em;white-space:nowrap}
.allocation-table-wrap td{padding:13px;border-right:1px solid #edf1f4;border-bottom:1px solid #e3eaf0;background:#fff;color:#334e68;font-size:11px!important;vertical-align:middle}
.allocation-table-wrap tbody tr:hover td{background:#f8fbfd}.allocation-table-wrap td b{color:#183a61;font-size:11px;line-height:1.35}.allocation-table-wrap td small{margin-top:4px;color:#72869a;font-size:9px!important;line-height:1.35}
.serial-number{width:28px;height:28px;display:inline-flex;align-items:center;justify-content:center;border-radius:50%;background:#0c3767;color:#fff;font-size:10px;font-weight:850}
.location-line{display:flex;align-items:flex-start;gap:6px}.location-line+ .location-line{margin-top:6px}.location-line>svg{flex:0 0 auto;color:#688098}.location-line.muted{color:#7b8d9f}
.allocation-badge{display:inline-flex;max-width:235px;padding:6px 9px;border-radius:99px;background:#fff0d6;color:#9b5c02;font-size:9px!important;font-weight:900;line-height:1.25}.allocation-badge.allocated{background:#dff6e7;color:#11713a}
.allocation-table-wrap select{min-width:190px}.update-button{min-height:39px;padding:0 13px;display:inline-flex;align-items:center;justify-content:center;gap:6px;border:1px solid var(--green);border-radius:7px;background:linear-gradient(180deg,#1ca153,#148842);color:#fff;font-size:10px!important;font-weight:850;white-space:nowrap;cursor:pointer;box-shadow:0 4px 9px rgba(21,148,71,.15)}
.update-button:hover{background:#117d3b}.update-button:disabled{opacity:.5;cursor:not-allowed}.allocation-table-wrap td em{font-size:8px!important}
.empty{padding:40px!important;text-align:center;color:var(--muted)}.allocation-panel footer{padding:11px 14px;display:flex;justify-content:space-between;gap:12px;border-top:1px solid var(--line);background:#f8fafc;color:#64798e;font-size:9px!important}
@media(max-width:1450px){.allocation-management-bar{grid-template-columns:1fr}.allocation-filters{grid-template-columns:minmax(260px,1.2fr) repeat(3,minmax(150px,.7fr)) auto}.allocation-kpis article{padding:13px}.allocation-kpis .kpi-icon{width:44px;height:44px;flex-basis:44px}}
@media(max-width:1120px){.allocation-kpis{grid-template-columns:repeat(3,1fr)}.allocation-filters{grid-template-columns:repeat(2,1fr)}.allocation-filters>button{align-self:end}.allocation-hero{align-items:flex-start;flex-direction:column}.hero-actions{width:100%;justify-content:flex-start}}
@media(max-width:720px){.allocation-register{gap:11px}.allocation-hero{padding:18px}.allocation-hero h1{font-size:25px}.hero-actions{display:grid;grid-template-columns:1fr 1fr}.live-data-card{grid-column:1/-1}.allocation-kpis{grid-template-columns:1fr}.allocation-filters{grid-template-columns:1fr}.district-filter-section{margin:10px}.district-filter-heading{align-items:flex-start;flex-direction:column}.district-filter-heading>em{align-self:flex-start}.district-filter-strip button{min-width:210px}.allocation-panel footer{flex-direction:column}}
`;
